import { useState } from "react";
import PaidIcon from "@mui/icons-material/Paid";
import CloseIcon from "@mui/icons-material/Close";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import { Button, Dialog, DialogActions, DialogContent, Grid, Typography } from "@mui/material";
import {
  Button as RaButton,
  minValue,
  NumberInput,
  required,
  SelectInput,
  useNotify,
  useRecordContext,
  useRefresh,
} from "react-admin";
import FormModal from "server/components/Modals/FormModal";
import ModalTitle, { withModalSx, modalCancelButtonSx, modalConfirmButtonSx } from "server/components/Modals/ModalTitle";
import dataProvider from "server/dataProvider";
import { debtsRequests } from "api/debtsApi";
import { CurrencyChoices, DebtPaymentProviderChoices } from "utils/choices";
import { DebtStatus } from "utils/constants";

const AddPaymentDialog = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();

  const [confirmOverpayment, setConfirmOverpayment] = useState(null);

  const currencyName = CurrencyChoices.find(c => c.id === record?.currency)?.name ?? "";

  const askOverpaymentConfirmation = () => new Promise(resolve => setConfirmOverpayment(() => resolve));

  const answerOverpayment = confirmed => {
    confirmOverpayment?.(confirmed);
    setConfirmOverpayment(null);
  };

  const createPayment = (amount, providerId, confirm) =>
    dataProvider.customRequest(
      debtsRequests.postDebtPayment(record?.id),
      { amount, providerId, confirmOverpayment: confirm },
      "POST",
    );

  const handleSubmit = async ({ amount, providerId }) => {
    const paymentAmount = Number(amount);

    // The backend compares the payment against the debt itself: below the debt amount it only records the
    // payment, equal to it marks the debt as paid, and above it nothing is recorded until we confirm the
    // overpayment — the excess then goes to the user's deposit.
    const { data } = await createPayment(paymentAmount, providerId, false);

    if (data?.requiresOverpaymentConfirmation) {
      const confirmed = await askOverpaymentConfirmation();

      // Declining closes the payment modal without anything being recorded.
      if (!confirmed) return;

      await createPayment(paymentAmount, providerId, true);
    }

    notify("Плащането е добавено успешно", { type: "success" });
    refresh();
  };

  if (record?.debtStatus !== DebtStatus.Unpaid) return null;

  return (
    <>
      <FormModal
        titleContent="Добавяне на плащане"
        saveTitle="Плати"
        onConfirm={handleSubmit}
        triggerButton={
          <RaButton
            label="Плащане"
            startIcon={<PaidIcon />}
            size="small"
          />
        }
      >
        <Grid
          container
          spacing={2}
        >
          <Grid
            item
            xs={12}
          >
            <Typography>
              {`Сума на задължението: ${Number(record?.amount ?? 0).toFixed(2)} ${currencyName}`.trim()}
            </Typography>
            <Typography>
              {`Платена сума: ${Number(record?.paidAmount ?? 0).toFixed(2)} ${currencyName}`.trim()}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            <NumberInput
              source="amount"
              label="Сума на плащането"
              fullWidth
              validate={[required(), minValue(0.01)]}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            <SelectInput
              source="providerId"
              label="Начин на плащане"
              choices={DebtPaymentProviderChoices}
              fullWidth
              validate={[required()]}
            />
          </Grid>
        </Grid>
      </FormModal>

      <Dialog
        open={!!confirmOverpayment}
        onClose={() => answerOverpayment(false)}
        fullWidth
        maxWidth="sm"
        sx={withModalSx()}
      >
        <ModalTitle
          icon={<PaidIcon />}
          title="Надплащане"
        />
        <DialogContent
          dividers
          sx={{ px: 4 }}
        >
          <Typography sx={{ p: 2 }}>
            Сумата надхвърля стойността на задължението. Остатъкът ще бъде качен на депозит на потребителя. Желаете ли
            да продължите?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "space-between", px: 3, py: 2 }}>
          <Button
            type="button"
            onClick={() => answerOverpayment(false)}
            variant="outlined"
            color="error"
            size="medium"
            startIcon={<CloseIcon />}
            sx={modalCancelButtonSx}
          >
            Отказ
          </Button>
          <Button
            type="button"
            onClick={() => answerOverpayment(true)}
            variant="contained"
            color="primary"
            size="medium"
            startIcon={<ThumbUpIcon />}
            sx={modalConfirmButtonSx}
          >
            Потвърди
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddPaymentDialog;
